import { useCallback } from 'react';
import { useEffectOnce } from 'react-use';

import { useChatSearch } from '../ChatSearchContext';
import type { TextConfig } from '../text-config';
import { clearAll, setAnswers } from './useAnswerList';
import { markdownTextBlock } from '../blocks/utils';
import { Answer } from '../types';

import { isAnswerUncertain } from '@/lib/utils';
import { loadConversation } from '@/lib/api';

interface UseLoadConversationProps {
  loadConversationEndpoint: string | undefined;
  headers?: Record<string, string>;
  mergedTextConfig: TextConfig;
}

/**
 * Hook to load a conversation by ID.
 * @param loadConversationEndpoint - The endpoint to use to load the conversation.
 * @param headers - The headers to use when loading the conversation.
 * @param mergedTextConfig - The text configuration to use.
 * @returns The function to load a conversation by ID.
 */
export const useConversationLoader = ({ loadConversationEndpoint, headers, mergedTextConfig }: UseLoadConversationProps) => {
  const {
    setIsLoadingConversation,
    setShowResults,
    answerListDispatch,
    conversationId,
    setConversationId,
    clearConversationId,
    setLoadError,
  } = useChatSearch();

  const loadConversationById = useCallback(
    (conversationId: string) => {
      if (!loadConversationEndpoint) {
        return;
      }

      setIsLoadingConversation(true);
      setIsLoadingConversation(true);
      setShowResults(true);

      const controller = new AbortController();

      loadConversation({
        endpoint: loadConversationEndpoint,
        conversationId,
        headers,
        signal: controller.signal,
      })
        .then(({ data }) => {
          const answers = data.map((query): Answer => {
            return {
              id: query.searchQueryId,
              state: 'completed',
              question: query.question,
              isUncertain: isAnswerUncertain(query.answer).uncertain,
              searchQueryId: query.searchQueryId,
              searchIntentId: query.searchIntentId,
              blocks: query.answer.split('---').map((text) =>
                markdownTextBlock({
                  text: text.trim(),
                }),
              ),
            };
          });
          answerListDispatch(setAnswers({ answers }));
          setConversationId(conversationId);
          setShowResults(true);
        })
        .catch((error) => {
          console.error('Error loading conversation:', error);
          answerListDispatch(clearAll());
          clearConversationId();
          setShowResults(false);
          setLoadError(
            mergedTextConfig.errorText || 'Sorry, we were unable to process the request right now. Please try again in a moment.',
          );
        })
        .finally(() => {
          setIsLoadingConversation(false);
        });

      return () => controller.abort('Conversation loader aborted');
    },
    [
      loadConversationEndpoint,
      setIsLoadingConversation,
      setShowResults,
      headers,
      answerListDispatch,
      setConversationId,
      setLoadError,
      mergedTextConfig.errorText,
    ],
  );

  useEffectOnce(() => {
    let cancel: (() => void) | undefined;
    if (conversationId) {
      // eslint-disable-next-line no-console
      console.log('loading conversation', conversationId);
      cancel = loadConversationById(conversationId);
    }

    return () => cancel?.();
  });

  return { loadConversationById };
};
